import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api from "@/lib/api";
import { PageHeader, Panel, EmptyState } from "@/components/admin/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QR_LOCATIONS, CHART_COLORS } from "@/lib/constants";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { Plus, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Campaigns() {
  const [codes, setCodes] = useState([]);
  const [perf, setPerf] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", location: "Main Gate", campaign: "" });

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/qrcodes"), api.get("/campaigns/performance")]);
    setCodes(c.data); setPerf(p.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => { await api.post("/qrcodes", form); toast.success("QR code created"); setOpen(false); setForm({ label: "", location: "Main Gate", campaign: "" }); load(); };
  const remove = async (code) => { await api.delete(`/qrcodes/${code}`); toast.success("Deleted"); load(); };

  const downloadQR = (code, label) => {
    const canvas = document.getElementById(`qr-${code}`);
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `QR-${label.replace(/\s/g, "-")}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <div>
      <PageHeader title="QR & Campaigns" subtitle="Track exactly where every visitor comes from">
        <Button onClick={() => setOpen(true)} data-testid="add-qr-btn" className="h-10 rounded-xl" style={{ background: "linear-gradient(135deg,#FFA14D,#E85D1F)", color: "#ffffff" }}><Plus size={16} /> New QR Code</Button>
      </PageHeader>

      {perf.length > 0 && (
        <Panel title="Campaign Performance" className="mb-5">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={perf}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" /><XAxis dataKey="campaign" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="visitors" fill="#0f2042" radius={[4, 4, 0, 0]} /><Bar dataKey="leads" fill="#E8752A" radius={[4, 4, 0, 0]} /><Bar dataKey="conversions" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {codes.length === 0 ? <EmptyState text="No QR codes yet" /> : codes.map((c) => {
          const url = `${window.location.origin}/register?qr=${encodeURIComponent(c.location)}`;
          return (
            <Panel key={c.code} data-testid="qr-card">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-display font-bold text-[#081226]">{c.label}</h3>
                  <p className="text-xs text-slate-400">{c.location}{c.campaign && ` · ${c.campaign}`}</p>
                </div>
                <button onClick={() => remove(c.code)} className="text-slate-300 hover:text-rose-500" data-testid="qr-delete-btn"><Trash2 size={16} /></button>
              </div>
              <div className="bg-white border border-slate-100 rounded-xl p-3 flex justify-center">
                <QRCodeCanvas id={`qr-${c.code}`} value={url} size={150} fgColor="#081226" level="M" />
              </div>
              <div className="flex items-center justify-between mt-3 text-sm">
                <div><p className="font-mono-num font-bold text-[#081226]">{c.visitors}</p><p className="text-xs text-slate-400">visitors</p></div>
                <Button variant="outline" size="sm" onClick={() => downloadQR(c.code, c.label)} data-testid="qr-download-btn" className="rounded-lg"><Download size={14} /> Download</Button>
              </div>
            </Panel>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>New QR Code</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-semibold">Label</label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Café Standee" data-testid="qr-label-input" /></div>
            <div><label className="text-sm font-semibold">Source / Location</label>
              <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger data-testid="qr-location-select"><SelectValue /></SelectTrigger>
                <SelectContent>{QR_LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-semibold">Campaign (optional)</label><Input value={form.campaign} onChange={(e) => setForm({ ...form, campaign: e.target.value })} placeholder="ONEIROS-INSTAGRAM" data-testid="qr-campaign-input" /></div>
          </div>
          <DialogFooter><Button onClick={create} disabled={!form.label} data-testid="qr-save-btn" className="btn-orange">Create QR</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
